import { useMemo } from 'react';

import PoolsHistoryCard from '@/components/global/PoolsHistoryCard';
import { formatNumberWithCommasAndDecimals } from '@/lib/utils/formatNumberWithCommasAndDecimals';
import { C_DECIMAL_PLACES } from '@/ts/constants/C_DECIMAL_PLACES';
import { T_UserBlockchainData } from '@/ts/types/T_UserBlockchainData';

interface I_UsePoolsDataProps {
  blockchainData?: T_UserBlockchainData;
  isEmptyState: boolean;
}

export function usePoolsData({
  blockchainData,
  isEmptyState
}: I_UsePoolsDataProps) {
  const poolsHistoryList = useMemo(() => {
    if (!blockchainData?.poolTransactions?.length || isEmptyState) {
      return null;
    }

    return (
      <div className="flex flex-col gap-4">
        {blockchainData.poolTransactions.map((transaction, idx: number) => {
          // Find matching pool to get current price and token symbol
          const matchingPool = blockchainData?.poolBalances?.find(
            pool =>
              pool?.pool?.coinId === transaction.pool.coinId ||
              pool?.pool?.poolAddress === transaction.pool.poolAddress
          );
          const currentPriceDecimal =
            matchingPool?.pool?.currentPriceDecimal || '0';
          // Use tokenSymbol from transaction pool
          const poolTokenSymbol = transaction?.pool?.tokenSymbol || '';

          // Calculate values
          const tokenAmount = parseFloat(transaction.tokenAmountDecimal || '0');
          const priceDecimal = parseFloat(transaction.priceDecimal || '0');
          const usdcAmount = parseFloat(transaction.usdcAmountDecimal || '0');
          const feeAmount = parseFloat(transaction.feeDecimal || '0');
          const currentPrice = parseFloat(currentPriceDecimal || '0');

          // Total cost = usdcAmount + feeDecimal
          const totalCost = usdcAmount + feeAmount;

          // Market value = tokenAmountDecimal * currentPriceDecimal
          const marketValue = tokenAmount * currentPrice;
          // Profit calculation: ((currentPrice - priceDecimal) / priceDecimal) * 100
          let profitPercent = 0;
          if (priceDecimal > 0) {
            profitPercent =
              ((currentPrice - priceDecimal) / priceDecimal) * 100;
          }

          const isBuy = transaction.type === 'BUY';

          return (
            <PoolsHistoryCard
              key={transaction.id || idx}
              type={isBuy ? 'buy' : 'sell'}
              boughtAt={formatNumberWithCommasAndDecimals(
                priceDecimal,
                C_DECIMAL_PLACES
              )}
              tradingPair={`USDC/${poolTokenSymbol}`}
              token={poolTokenSymbol}
              tokens={formatNumberWithCommasAndDecimals(
                tokenAmount,
                C_DECIMAL_PLACES
              )}
              totalCost={formatNumberWithCommasAndDecimals(
                totalCost,
                C_DECIMAL_PLACES
              )}
              marketValue={formatNumberWithCommasAndDecimals(
                marketValue,
                C_DECIMAL_PLACES
              )}
              profit={profitPercent}
              blockTimestamp={transaction.blockTimestamp}
            />
          );
        })}
      </div>
    );
  }, [blockchainData, isEmptyState]);

  return {
    poolsHistoryList
  };
}
